import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FaLock, FaUser, FaExclamationCircle } from "react-icons/fa";
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const SignIn = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, currentUser, isEmailVerified } = useAuth();

  // Redirect if user is already logged in and verified
  useEffect(() => {
    if (currentUser) {
      console.log("Current user state:", { currentUser, isEmailVerified });
      if (isEmailVerified) {
        // Redirect to the originally attempted URL or dashboard
        console.log("User verified, redirecting to dashboard");
        const from = (location.state as any)?.from?.pathname || '/dashboard';
        navigate(from);
      } else {
        // User not verified, redirect to verify email page
        console.log("User not verified, redirecting to verify email page");
        navigate('/auth/verify-email');
      }
    }
  }, [currentUser, isEmailVerified, navigate, location]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      setError('');
      setLoading(true);
      console.log("Attempting login with:", email);
      await login(email, password);
      console.log("Login successful");
      // Navigation will be handled by the useEffect above
    } catch (err: any) {
      console.error("Sign in error:", err);
      setError('Wrong credentials. Please try again.');
      toast.error('Sign in failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 py-6 px-2 sm:px-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <img 
            src="/TRUTH NEXUS.svg" 
            alt="Truth Nexus Logo" 
            className="h-20 mx-auto mb-4"
          />
          <h1 className="text-2xl sm:text-3xl font-bold text-truth">Sign In</h1>
          <p className="mt-2 text-gray-400">Access your account</p>
        </div>
        <Card className="bg-black/20 backdrop-blur-sm border-gray-800">
          <CardHeader>
            <CardTitle className="text-lg sm:text-xl">Sign In</CardTitle>
            <CardDescription className="text-xs sm:text-sm">Welcome back! Please enter your credentials.</CardDescription>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              {error && (
                <div className="p-3 bg-red-900/30 border border-red-800 rounded-md flex items-center gap-2 text-sm">
                  <FaExclamationCircle className="text-red-500" />
                  <span>{error}</span>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <FaUser className="text-gray-500" />
                  </div>
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@example.com" 
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link to="/auth/forgot-password" className="text-sm text-truth hover:underline">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <FaLock className="text-gray-500" />
                  </div>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••" 
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="remember"
                  className="rounded border-gray-700 bg-gray-800 text-truth focus:ring-truth"
                />
                <Label htmlFor="remember" className="text-sm">Remember me</Label>
              </div>
            </CardContent>
            <CardFooter className="flex flex-col space-y-4">
              <Button 
                type="submit" 
                className="w-full bg-truth hover:bg-truth-dark"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </div>
                ) : "Sign In"}
              </Button>
              
              <p className="text-center text-sm text-gray-400">
                Don't have an account?{' '}
                <Link to="/auth/register" className="text-truth hover:underline">Register</Link>
              </p>
            </CardFooter>
          </form>
        </Card>
        <div className="text-center">
          <Link to="/" className="text-sm text-gray-400 hover:text-truth">Back to home</Link>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
